import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, InputAdornment, Popover, TextField } from '@mui/material'
import {
  Close as CloseIcon,
  EmojiEmotionsOutlined as EmojiIcon,
} from '@mui/icons-material'
import { LazyEmojiPicker } from './LazyEmojiPicker'

interface EmojiFieldProps {
  label: string
  value: string
  onChange: (emoji: string) => void
  size?: 'small' | 'medium'
  width?: number
}

/**
 * Поле выбора одного emoji: readonly-инпут + Popover с полным picker
 * (общий lazy-чанк с композером чата). Клик, Enter или Space открывают
 * picker, крестик очищает значение.
 */
export function EmojiField({ label, value, onChange, size = 'small', width = 140 }: EmojiFieldProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handlePick = (emoji: string) => {
    onChange(emoji)
    setAnchorEl(null)
  }

  return (
    <>
      <TextField
        label={label}
        value={value}
        size={size}
        sx={{ width }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setAnchorEl(event.currentTarget)
          }
        }}
        InputProps={{
          readOnly: true,
          sx: { cursor: 'pointer' },
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  size="small"
                  aria-label={t('common.clear')}
                  onClick={(event) => {
                    event.stopPropagation()
                    onChange('')
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : (
                <EmojiIcon fontSize="small" color="action" />
              )}
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <LazyEmojiPicker onPick={handlePick} />
      </Popover>
    </>
  )
}
